(() => {
  'use strict';

  if (window.RukiClientGallery) {
    window.RukiClientGallery.init(document);
    return;
  }

  const ROOT = '[data-ruki-clients]';
  const CARD = '[data-ruki-card]';
  const MAX_PAGES = 43;
  const instances = new Map();
  let activeLightbox = null;

  function positiveInteger(value, fallback) {
    const number = Number.parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  class ClientGallery {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-ruki-grid]');
      this.button = root.querySelector('[data-ruki-load-more]');
      this.nextLink = root.querySelector('[data-ruki-next]');
      this.empty = root.querySelector('[data-ruki-empty]');
      this.status = root.querySelector('[data-ruki-status]');
      this.dialog = root.querySelector('[data-ruki-lightbox]');
      this.sectionId = root.dataset.sectionId || '';
      this.teaser = root.dataset.mode === 'teaser';
      this.step = positiveInteger(root.dataset.step, 12);
      this.target = positiveInteger(root.dataset.target, 3);
      this.strings = {
        loading: root.dataset.i18nLoading || 'Cargando experiencias…',
        load: root.dataset.i18nLoad || 'Cargar más',
        error: root.dataset.i18nError || 'No se pudo cargar. Vuelve a intentarlo o usa el enlace siguiente.',
        empty: root.dataset.i18nEmpty || 'Aún no hay experiencias disponibles.',
        retry: root.dataset.i18nRetry || 'Reintentar',
      };
      this.cards = this.grid ? Array.from(this.grid.querySelectorAll(CARD)) : [];
      this.cardIds = new Set(this.cards.map((card) => card.dataset.rukiCard).filter(Boolean));
      this.nextUrl = this.nextLink ? this.nextLink.getAttribute('href') : null;
      this.wantedCount = this.teaser ? this.target : this.step;
      this.visibleCount = 0;
      this.pageCount = 1;
      this.seenUrls = new Set([this.pageKey(window.location.href)]);
      this.busy = false;
      this.failed = false;
      this.fetchSucceeded = false;
      this.destroyed = false;
      this.listeners = [];
      this.canFetch = Boolean(this.sectionId && window.fetch && window.DOMParser && window.AbortController);

      if (!this.grid) return;
      this.root.dataset.rukiEnhanced = 'true';
      this.cards.forEach((card, index) => this.prepareCard(card, index >= this.wantedCount));
      this.listen(root, 'click', (event) => this.onClick(event));
      if (this.dialog) this.setupLightbox();
      this.update();

      if (this.canFetch && this.nextUrl && (this.cards.length === 0 || (this.teaser && this.cards.length < this.target))) {
        this.fill(this.wantedCount);
      }
    }

    listen(element, type, listener) {
      element.addEventListener(type, listener);
      this.listeners.push(() => element.removeEventListener(type, listener));
    }

    pageKey(value) {
      const url = new URL(value, window.location.href);
      url.hash = '';
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      url.searchParams.sort();
      return url.href;
    }

    prepareCard(card, deferred) {
      card.querySelectorAll('img').forEach((image) => {
        if (deferred || image.loading !== 'eager') image.loading = 'lazy';
        image.decoding = 'async';
      });
    }

    update() {
      if (this.destroyed) return;
      this.visibleCount = Math.min(this.wantedCount, this.cards.length);
      this.cards.forEach((card, index) => { card.hidden = index >= this.visibleCount; });
      this.grid.setAttribute('aria-busy', String(this.busy));
      const buffered = this.cards.length > this.visibleCount;
      if (this.button) {
        this.button.hidden = this.teaser || !(buffered || (this.canFetch && this.nextUrl));
        this.button.disabled = this.busy;
        this.button.textContent = this.busy ? this.strings.loading : (this.failed ? this.strings.retry : this.strings.load);
      }
      if (this.nextLink) {
        if (this.nextUrl) this.nextLink.setAttribute('href', this.nextUrl);
        else this.nextLink.removeAttribute('href');
        this.nextLink.hidden = !this.nextUrl || (this.fetchSucceeded && !this.failed);
      }
      if (this.empty) {
        this.empty.hidden = this.cards.length > 0 || this.busy || Boolean(this.nextUrl) || this.failed;
      }
      if (this.status) {
        this.status.textContent = this.busy ? this.strings.loading
          : this.failed ? this.strings.error
            : (!this.empty && !this.cards.length && !this.nextUrl ? this.strings.empty : '');
      }
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest(ROOT) !== this.root) return;
      if (target.closest('[data-ruki-load-more]') === this.button && this.button) {
        event.preventDefault();
        const count = this.failed ? this.wantedCount : this.visibleCount + this.step;
        this.fill(count);
        return;
      }
      const photo = target.closest('[data-ruki-photo-open]');
      if (photo && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey && event.button === 0) {
        if (this.openPhoto(photo)) event.preventDefault();
      }
    }

    async fill(count) {
      if (this.busy || this.destroyed) return;
      this.wantedCount = this.teaser ? this.target : count;
      this.failed = false;
      this.busy = true;
      this.update();
      try {
        while (this.cards.length < this.wantedCount && this.nextUrl && this.canFetch) {
          await this.fetchBatch();
          if (this.destroyed) return;
          this.update();
        }
      } catch (error) {
        if (!this.destroyed) this.failed = true;
      } finally {
        if (!this.destroyed) {
          this.busy = false;
          this.update();
        }
      }
    }

    async fetchBatch() {
      const pageUrl = new URL(this.nextUrl, window.location.href);
      if (pageUrl.origin !== window.location.origin || !/^https?:$/.test(pageUrl.protocol)) {
        throw new Error('Invalid gallery URL');
      }
      const key = this.pageKey(pageUrl.href);
      if (this.seenUrls.has(key) || this.pageCount >= MAX_PAGES) {
        throw new Error('Gallery pagination limit');
      }
      pageUrl.searchParams.delete('sections');
      pageUrl.searchParams.set('section_id', this.sectionId);
      const controller = new AbortController();
      this.request = controller;
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(pageUrl.href, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Gallery request failed');
        const html = await response.text();
        if (this.destroyed) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const incomingRoot = Array.from(documentFragment.querySelectorAll(ROOT))
          .find((element) => element.dataset.sectionId === this.sectionId);
        const incomingGrid = incomingRoot && incomingRoot.querySelector('[data-ruki-grid]');
        if (!incomingGrid) throw new Error('Invalid gallery response');
        const incomingLink = incomingRoot.querySelector('[data-ruki-next]');
        const incomingHref = incomingLink && incomingLink.getAttribute('href');
        let next = null;
        if (incomingHref) {
          const nextPage = new URL(incomingHref, pageUrl.href);
          if (nextPage.origin !== window.location.origin || !/^https?:$/.test(nextPage.protocol)) {
            throw new Error('Invalid next gallery URL');
          }
          nextPage.searchParams.delete('section_id');
          nextPage.searchParams.delete('sections');
          next = nextPage.href;
        }
        const additions = document.createDocumentFragment();
        incomingGrid.querySelectorAll(CARD).forEach((sourceCard) => {
          const id = sourceCard.dataset.rukiCard;
          if (id && this.cardIds.has(id)) return;
          const card = document.importNode(sourceCard, true);
          card.hidden = true;
          this.prepareCard(card, true);
          if (id) this.cardIds.add(id);
          this.cards.push(card);
          additions.append(card);
        });
        this.grid.append(additions);
        this.seenUrls.add(key);
        this.pageCount += 1;
        this.nextUrl = next;
        this.fetchSucceeded = true;
      } finally {
        window.clearTimeout(timeout);
        if (this.request === controller) this.request = null;
      }
    }

    setupLightbox() {
      this.lightboxImage = this.dialog.querySelector('[data-ruki-lightbox-image]');
      this.lightboxCaption = this.dialog.querySelector('[data-ruki-lightbox-caption]');
      this.lightboxCount = this.dialog.querySelector('[data-ruki-lightbox-count]');
      this.previousPhoto = this.dialog.querySelector('[data-ruki-lightbox-prev]');
      this.nextPhoto = this.dialog.querySelector('[data-ruki-lightbox-next]');
      this.closePhoto = this.dialog.querySelector('[data-ruki-lightbox-close]');
      this.listen(this.dialog, 'close', () => {
        if (!this.dialog.open) this.restoreLightbox();
      });
      this.listen(this.dialog, 'pointerdown', (event) => {
        this.backdropPointer = this.isBackdrop(event);
      });
      this.listen(this.dialog, 'click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (target.closest('[data-ruki-lightbox-close]') || (this.backdropPointer && this.isBackdrop(event))) {
          event.preventDefault();
          this.closeLightbox();
        } else if (target.closest('[data-ruki-lightbox-prev]')) {
          event.preventDefault();
          this.movePhoto(-1);
        } else if (target.closest('[data-ruki-lightbox-next]')) {
          event.preventDefault();
          this.movePhoto(1);
        }
        this.backdropPointer = false;
      });
      this.listen(this.dialog, 'keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.movePhoto(event.key === 'ArrowLeft' ? -1 : 1);
        }
      });
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rectangle = this.dialog.getBoundingClientRect();
      return event.clientX < rectangle.left || event.clientX > rectangle.right
        || event.clientY < rectangle.top || event.clientY > rectangle.bottom;
    }

    openPhoto(anchor) {
      if (!this.dialog || !this.lightboxImage || typeof this.dialog.showModal !== 'function') return false;
      const photos = [];
      const seen = new Set();
      this.cards.filter((card) => !card.hidden).forEach((card) => {
        card.querySelectorAll('[data-ruki-photo-open]').forEach((photo) => {
          const url = new URL(photo.href, window.location.href);
          if (!/^https?:$/.test(url.protocol) || seen.has(url.href)) return;
          seen.add(url.href);
          photos.push(photo);
        });
      });
      const index = photos.findIndex((photo) => photo.href === anchor.href);
      if (index < 0) return false;
      if (activeLightbox) activeLightbox.closeLightbox();
      this.photos = photos;
      this.photoIndex = index;
      this.returnFocus = anchor;
      this.savedScroll = { x: window.scrollX, y: window.scrollY };
      this.dialogWasHidden = this.dialog.hidden;
      this.dialog.hidden = false;
      this.renderPhoto();
      try {
        this.dialog.showModal();
      } catch (error) {
        this.dialog.hidden = this.dialogWasHidden;
        return false;
      }
      this.lockScroll();
      activeLightbox = this;
      if (this.closePhoto) this.closePhoto.focus({ preventScroll: true });
      return true;
    }

    renderPhoto() {
      const photo = this.photos[this.photoIndex];
      this.lightboxImage.src = photo.href;
      this.lightboxImage.alt = photo.dataset.alt || '';
      if (this.lightboxCaption) this.lightboxCaption.textContent = photo.dataset.caption || '';
      if (this.lightboxCount) this.lightboxCount.textContent = `${this.photoIndex + 1} / ${this.photos.length}`;
      if (this.previousPhoto) this.previousPhoto.disabled = this.photos.length < 2;
      if (this.nextPhoto) this.nextPhoto.disabled = this.photos.length < 2;
    }

    movePhoto(direction) {
      if (!this.photos || this.photos.length < 2) return;
      this.photoIndex = (this.photoIndex + direction + this.photos.length) % this.photos.length;
      this.renderPhoto();
    }

    lockScroll() {
      const body = document.body;
      const properties = ['position', 'top', 'left', 'right', 'width', 'overflow', 'padding-right', 'box-sizing'];
      this.bodyStyle = properties.map((property) => ({
        property,
        value: body.style.getPropertyValue(property),
        priority: body.style.getPropertyPriority(property),
      }));
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const padding = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.setProperty('position', 'fixed');
      body.style.setProperty('top', `${-this.savedScroll.y}px`);
      body.style.setProperty('left', `${-this.savedScroll.x}px`);
      body.style.setProperty('right', '0');
      body.style.setProperty('width', '100%');
      body.style.setProperty('overflow', 'hidden');
      body.style.setProperty('padding-right', `${padding + scrollbar}px`);
      body.style.setProperty('box-sizing', 'border-box');
    }

    closeLightbox() {
      if (this.dialog && this.dialog.open) this.dialog.close();
      this.restoreLightbox();
    }

    restoreLightbox() {
      if (!this.bodyStyle) return;
      this.bodyStyle.forEach(({ property, value, priority }) => {
        if (value) document.body.style.setProperty(property, value, priority);
        else document.body.style.removeProperty(property);
      });
      this.bodyStyle = null;
      if (activeLightbox === this) activeLightbox = null;
      this.dialog.hidden = this.dialogWasHidden;
      this.lightboxImage.removeAttribute('src');
      if (this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus({ preventScroll: true });
      const htmlStyle = document.documentElement.style;
      const behavior = htmlStyle.getPropertyValue('scroll-behavior');
      const priority = htmlStyle.getPropertyPriority('scroll-behavior');
      htmlStyle.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(this.savedScroll.x, this.savedScroll.y);
      if (behavior) htmlStyle.setProperty('scroll-behavior', behavior, priority);
      else htmlStyle.removeProperty('scroll-behavior');
    }

    destroy() {
      this.destroyed = true;
      if (this.request) this.request.abort();
      this.closeLightbox();
      this.listeners.forEach((remove) => remove());
      this.cards.forEach((card) => { card.hidden = false; });
      if (this.button) this.button.hidden = true;
      if (this.nextLink) this.nextLink.hidden = !this.nextUrl;
      if (this.empty) this.empty.hidden = this.cards.length > 0 || Boolean(this.nextUrl);
      if (this.grid) this.grid.removeAttribute('aria-busy');
      if (this.status) this.status.textContent = '';
      delete this.root.dataset.rukiEnhanced;
    }
  }

  function init(scope) {
    instances.forEach((instance, root) => {
      if (!root.isConnected) {
        instance.destroy();
        instances.delete(root);
      }
    });
    const roots = Array.from(scope.querySelectorAll(ROOT));
    if (scope instanceof Element && scope.matches(ROOT)) roots.unshift(scope);
    roots.forEach((root) => {
      if (!instances.has(root)) instances.set(root, new ClientGallery(root));
    });
  }

  window.RukiClientGallery = { init };
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, root) => {
      if (event.target === root || event.target.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
